/**
 * @remarks RuntimeDocumentV1 i18n 映射与组件数据绑定合同
 * @module @yimoka/designable-playground/runtime
 */

import { UNSAFE_SCHEMA_OBJECT_KEYS } from '../shared/schema';

import {
  DATA_BINDING_MANUAL_KEYS,
  isObjectRecord,
  pushRuntimeIssue,
  RUNTIME_OPTION_SCHEMA_KEYS,
} from './document-core';
import type { IRuntimeDocumentValidationIssue } from './document-core';
import { isAllowedRuntimeDataKeyPath } from './document-expressions';

/** 校验 Entity 多语言字段映射的非空、安全和一对一约束。 */
export const validateRuntimeI18nFieldMap = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) {
    pushRuntimeIssue(issues, 'invalid-i18n-field-map', path, 'i18nFieldMap 必须是对象');
    return;
  }

  const targetFields = new Set<string>();
  Object.entries(value).forEach(([sourceField, targetField]) => {
    if (
      !sourceField.trim()
      || sourceField.trim() !== sourceField
      || UNSAFE_SCHEMA_OBJECT_KEYS.has(sourceField)
      || typeof targetField !== 'string'
      || !targetField.trim()
      || targetField.trim() !== targetField
      || UNSAFE_SCHEMA_OBJECT_KEYS.has(targetField)
      || targetFields.has(targetField)
    ) {
      pushRuntimeIssue(
        issues,
        'invalid-i18n-field-map',
        `${path}.${sourceField}`,
        '多语言值字段和单语言字段必须是安全、非空且一对一的字符串',
      );
      return;
    }
    targetFields.add(targetField);
  });
};

/** 判断数据绑定属性是否携带会参与运行时取数的非空值。 */
const isNonEmptyDataBindingValue = (value: unknown) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  if (isObjectRecord(value)) return Object.keys(value).length > 0;
  return true;
};

/** 判断手填数据属性是否会遮蔽 Store 取数；空数组和 null 也会被运行时优先采用。 */
const isDefinedManualDataBindingValue = (value: unknown) => value !== undefined;

/** 读取 Transfer 枚举条目的实际键；普通枚举值直接作为键，对象条目沿用共享归一优先级。 */
const getRuntimeTransferOptionKey = (
  item: unknown,
  optionKey: typeof RUNTIME_OPTION_SCHEMA_KEYS[number],
) => {
  if (!isObjectRecord(item)) return item;
  const candidateKeys = optionKey === 'oneOf'
    ? ['const', 'value']
    : ['value', 'const', 'key'];
  const candidate = candidateKeys.find(key => Object.prototype.hasOwnProperty.call(item, key));
  return candidate === undefined ? undefined : item[candidate];
};

/** Transfer 只能消费 React.Key；在导入边界拒绝会被数据源字符串化且无法匹配 targetKeys 的枚举值。 */
const validateRuntimeTransferOptionKeys = (
  value: Record<string, unknown>,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  RUNTIME_OPTION_SCHEMA_KEYS.forEach((optionKey) => {
    const source = value[optionKey];
    if (!Array.isArray(source)) return;
    source.forEach((item, index) => {
      const key = getRuntimeTransferOptionKey(item, optionKey);
      if (key === undefined || typeof key === 'string' || typeof key === 'number') return;
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.${optionKey}[${index}]`,
        'Transfer 枚举值必须是字符串或数字',
      );
    });
  });
};

/** 校验单个静态组件节点的 manual/store 数据字段互斥关系。 */
export const validateRuntimeDataBindingNode = (
  value: Record<string, unknown>,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  componentName: string | undefined,
) => {
  if (!componentName) return;
  const manualKeys = DATA_BINDING_MANUAL_KEYS[componentName];
  if (!manualKeys) return;
  if (componentName === 'Transfer') {
    validateRuntimeTransferOptionKeys(value, issues, path);
  }

  const componentProps = value['x-component-props'];
  if (componentProps === undefined) return;
  if (!isObjectRecord(componentProps)) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.x-component-props`,
      'x-component-props 必须是对象',
    );
    return;
  }

  const activeManualKeys = manualKeys.filter(key => (
    isDefinedManualDataBindingValue(componentProps[key])
  ));
  const activeSchemaOptionKeys = RUNTIME_OPTION_SCHEMA_KEYS.filter(key => (
    isDefinedManualDataBindingValue(value[key])
  ));
  const { dataKey } = componentProps;
  if (
    dataKey !== undefined
    && dataKey !== null
    && typeof dataKey !== 'string'
  ) {
    pushRuntimeIssue(
      issues,
      'invalid-data-key',
      `${path}.x-component-props.dataKey`,
      `${componentName} 的 dataKey 必须是字符串`,
    );
  }
  if (
    typeof dataKey === 'string'
    && dataKey.length > 0
    && dataKey.trim() !== dataKey
  ) {
    pushRuntimeIssue(
      issues,
      'invalid-data-key',
      `${path}.x-component-props.dataKey`,
      `${componentName} 的 dataKey 不能包含首尾空白`,
    );
  }
  if (typeof dataKey === 'string') {
    if (dataKey.trim() === dataKey && !isAllowedRuntimeDataKeyPath(dataKey)) {
      pushRuntimeIssue(
        issues,
        'invalid-data-key',
        `${path}.x-component-props.dataKey`,
        `${componentName} 的 dataKey 只能读取 Store 数据命名空间且不能包含原型链路径段`,
      );
    }
  }
  if (
    isNonEmptyDataBindingValue(dataKey)
    && (activeManualKeys.length > 0 || activeSchemaOptionKeys.length > 0)
  ) {
    const conflicts = [...activeManualKeys, ...activeSchemaOptionKeys];
    pushRuntimeIssue(
      issues,
      'conflicting-data-binding',
      `${path}.x-component-props`,
      `${componentName} 的 dataKey 不能与 ${conflicts.join('/')} 同时存在`,
    );
  }
  if (activeManualKeys.length > 1) {
    pushRuntimeIssue(
      issues,
      'conflicting-manual-data-binding',
      `${path}.x-component-props`,
      `${componentName} 只能保留一个手填数据字段`,
    );
  }
};
